import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getHubspotFieldMappings,
  updateHubspotFieldMapping,
} from '../api/fieldMappings';
import { completeHubspotSetup } from '../api/setup';
import type {
  HubspotFieldMapping,
  HubspotFieldMappingsResponse,
} from '../types';

interface UseHubspotMappingsOptions {
  /**
   * Gate the field-mappings fetch. Field mappings only exist once connected,
   * so callers should pass `false` until then — otherwise the query fetches
   * prematurely (no connection yet), caches an empty/404 result, and the UI
   * shows "No field mappings found" even after they're created. Defaults to
   * true to preserve the post-setup manager's behavior.
   */
  enabled?: boolean;
}

export function useHubspotMappings(
  orgId: number | string | undefined,
  options?: UseHubspotMappingsOptions
) {
  const queryClient = useQueryClient();

  const mappingsQuery = useQuery<HubspotFieldMappingsResponse>({
    queryKey: ['hubspotMappings', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getHubspotFieldMappings(orgId);
    },
    enabled: !!orgId && (options?.enabled ?? true),
  });

  const updateMapping = useMutation({
    mutationFn: ({
      mappingId,
      body,
    }: {
      mappingId: number;
      body: Parameters<typeof updateHubspotFieldMapping>[2];
    }) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return updateHubspotFieldMapping(orgId, mappingId, body);
    },
    onSuccess: (_data: HubspotFieldMapping) =>
      queryClient.invalidateQueries({ queryKey: ['hubspotMappings', orgId] }),
  });

  const completeSetup = useMutation({
    mutationFn: (backfill: boolean) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return completeHubspotSetup(orgId, backfill);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['hubspotStatus', orgId] }),
  });

  return useMemo(
    () => ({
      mappings: mappingsQuery,
      isLoading: mappingsQuery.isLoading,
      error: mappingsQuery.error,
      updateMapping,
      completeSetup,
    }),
    [mappingsQuery, updateMapping, completeSetup]
  );
}
